import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toKebabCase } from '@klareospace/pharos-lib-utils';
import type { VehicleTemplate } from '@klareospace/pharos-lib-types';
import { VEHICLE_PROFILES } from '@klareospace/pharos-lib-types';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SideDrawer,
  VehicleProfileIcon,
} from '@klareospace/pharos-lib-web-utils';

import { useVehicleTemplateForm } from '../hooks';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VehicleTemplateDrawerProps {
  onClose: () => void;
  onCreated?: (template: VehicleTemplate) => void;
  open: boolean;
  vehicleTemplate: VehicleTemplate | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function VehicleTemplateDrawer({
  onClose,
  onCreated,
  open,
  vehicleTemplate,
}: VehicleTemplateDrawerProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const isEditMode = vehicleTemplate !== null;

  const { form, isPending, onSubmit } = useVehicleTemplateForm(vehicleTemplate, { onCreated, onSuccess: onClose });

  const codeManuallyEdited = useRef(false);

  useEffect(() => {
    if (open && !isEditMode) {
      codeManuallyEdited.current = false;
    }
  }, [open, isEditMode]);

  // Any close path (backdrop, Escape, or a parent-initiated close via the `open` prop) must discard
  // a typed draft — otherwise it survives and reappears the next time the drawer opens.
  useEffect(() => {
    if (!open) form.reset();
  }, [open, form]);

  const title = isEditMode ? t('vehicleTemplates.drawer.editTitle') : t('vehicleTemplates.drawer.createTitle');
  const subtitle = isEditMode
    ? t('vehicleTemplates.drawer.editDescription')
    : t('vehicleTemplates.drawer.createDescription');

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      form.reset();
      onClose();
    }
  }

  function handleNameChange(
    event: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  ): void {
    fieldOnChange(event);
    if (!isEditMode && !codeManuallyEdited.current) {
      form.setValue('code', toKebabCase(event.target.value), { shouldValidate: true });
    }
  }

  function handleCodeChange(
    event: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  ): void {
    codeManuallyEdited.current = true;
    fieldOnChange(event);
  }

  const footer = (
    <>
      <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type="button">
        {isEditMode ? t('vehicleTemplates.form.submitEdit') : t('vehicleTemplates.form.submit')}
      </Button>
      <Button onClick={(): void => handleOpenChange(false)} type="button" variant="outline">
        {t('vehicleTemplates.form.cancel')}
      </Button>
    </>
  );

  return (
    <SideDrawer
      dismissOnOutsideClick={false}
      footer={footer}
      onOpenChange={handleOpenChange}
      open={open}
      subtitle={subtitle}
      title={title}
    >
      <Form {...form}>
        <form className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {t('vehicleTemplates.form.label')} <span>*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e): void => handleNameChange(e, field.onChange)}
                    placeholder={t('vehicleTemplates.form.labelPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="code"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {t('vehicleTemplates.form.code')} <span>*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="font-mono text-xs"
                    onChange={(e): void => handleCodeChange(e, field.onChange)}
                    placeholder={t('vehicleTemplates.form.codePlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="profile"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {t('vehicleTemplates.form.vehicleProfile')} <span>*</span>
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={VEHICLE_PROFILES.PEDESTRIAN}>
                      <span className="flex items-center gap-2">
                        <VehicleProfileIcon className="h-4 w-4" profile={VEHICLE_PROFILES.PEDESTRIAN} />
                        {t('vehicleTemplates.profiles.pedestrian')}
                      </span>
                    </SelectItem>
                    <SelectItem value={VEHICLE_PROFILES.BICYCLE}>
                      <span className="flex items-center gap-2">
                        <VehicleProfileIcon className="h-4 w-4" profile={VEHICLE_PROFILES.BICYCLE} />
                        {t('vehicleTemplates.profiles.bicycle')}
                      </span>
                    </SelectItem>
                    <SelectItem value={VEHICLE_PROFILES.LIGHTWEIGHT_VEHICLE}>
                      <span className="flex items-center gap-2">
                        <VehicleProfileIcon className="h-4 w-4" profile={VEHICLE_PROFILES.LIGHTWEIGHT_VEHICLE} />
                        {t('vehicleTemplates.profiles.lightweight-vehicle')}
                      </span>
                    </SelectItem>
                    <SelectItem value={VEHICLE_PROFILES.LARGE_VEHICLE}>
                      <span className="flex items-center gap-2">
                        <VehicleProfileIcon className="h-4 w-4" profile={VEHICLE_PROFILES.LARGE_VEHICLE} />
                        {t('vehicleTemplates.profiles.large-vehicle')}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="maxVolumeValue"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {t('vehicleTemplates.form.maxVolumeCapacity')} <span>*</span>
                </FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <Input {...field} className="flex-1" min={0} placeholder="0" step={1} type="number" />
                  </FormControl>
                  <FormField
                    control={form.control}
                    name="maxVolumeUnit"
                    render={({ field: unitField }): React.JSX.Element => (
                      <Select onValueChange={unitField.onChange} value={unitField.value}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="m3">{tCommon('units.m3')}</SelectItem>
                          <SelectItem value="L">{tCommon('units.L')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="maxWeightValue"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {t('vehicleTemplates.form.maxWeightCapacity')} <span>*</span>
                </FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <Input {...field} className="flex-1" min={0} placeholder="0" step={1} type="number" />
                  </FormControl>
                  <FormField
                    control={form.control}
                    name="maxWeightUnit"
                    render={({ field: unitField }): React.JSX.Element => (
                      <Select onValueChange={unitField.onChange} value={unitField.value}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kg">{tCommon('units.kg')}</SelectItem>
                          <SelectItem value="lb">{tCommon('units.lb')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="maxOrderCapacity"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>{t('vehicleTemplates.form.maxOrderCapacity')}</FormLabel>
                <FormControl>
                  <Input {...field} min={1} placeholder="1" step={1} type="number" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </SideDrawer>
  );
}
